<template>
  <div class="bordered-item" id="BookingForm" v-if="!packageData?.IsSoldOut && !packageData?.IsComingSoon">
    <div class="bordered-item-wrapper">
      <div class="generic-form-container">
        <div class="generic-form-header">
          <h2 class="text-uppercase">Secure your experience now</h2>
          <p class="text-light-grey">Choose Your Package &amp; Quantity</p>
        </div>
        <div class="generic-form">
          <form @submit.prevent="submit" novalidate>
            <div class="input-holder wide has-dropdown">
              <label for="GameIds">Fixture*</label>
              <CustomMultiSelect id="GameIds" name="GameIds" v-model="request.GameIds" :options="gameOptions"
                :display="gameOptions.length > 1 ? 'chip' : 'comma'" :option-label="item => formatGame(gameMetadata[item.GameId])"
                :option-disabled="item => isGameDisabled(gameMetadata[item.GameId])" option-value="GameId" placeholder="Select fixtures"
                :disabled="gameOptions.length === 1" :class="{ 'p-multiselect-readonly': gameOptions.length === 1 }" append-to="self">
              </CustomMultiSelect>
              <small class="p-error" v-if="form.GameIds.$error">{{ form.GameIds.$errors[0].$message }}</small>
            </div>
            <hr class="white-fade" v-if="gameOptions.length > 1" />

            <div v-for="(game, index) in request.Games" :key="game.GameId">
              <StartBookingGameForm v-model="request.Games[index]" :index="index" :game-metadata="gameMetadata[game.GameId]"
                :game-count="gameOptions.length" :isInternational="packageData?.DomesticInternational == 'International'" />
            </div>
            <div v-if="!!packageData?.PackageExperienceInventory.length">
              <div class="input-holder wide has-dropdown">
                <label for="ExperienceInventoryIds">Additional Experiences</label>
                <div class="select-holder">
                  <CustomMultiSelect id="ExperienceInventoryIds" name="ExperienceInventoryIds" v-model="request.PackageExperienceInventoryIds"
                    display="chip" option-value="InventoryId" :option-label="formatExperience" :option-disabled="item => item.IsSoldOut"
                    :options="packageData.PackageExperienceInventory" placeholder="Per fixture options" show-clear append-to="self">
                    <template #option="{ option }">
                      <span>{{ option.DisplayName }}: {{ option.FormattedDateRange }} ({{ formatCurrency(option.UnitRrpExclGst) }} pp) <span
                          v-if="option.IsSoldOut">- SOLD OUT</span></span>
                    </template>
                  </CustomMultiSelect>
                </div>
                <small class="p-error"
                  v-if="form.PackageExperienceInventoryIds.$error">{{ form.PackageExperienceInventoryIds.$errors[0].$message }}</small>
              </div>

              <div v-if="request.PackageExperiences.length">
                <div class="asymmetric-input-group d-flex flex-v-centre no-lines desktop">
                  <div class="sub-option"></div>
                  <div class="input-holder">Number of People*</div>
                </div>
                <StartBookingExperienceForm v-for="(experience, index) in request.PackageExperiences" :key="experience.ExperienceInventoryId"
                  v-model="request.PackageExperiences[index]" :experience-metadata="experienceMetadata[experience.ExperienceInventoryId]"
                  @remove="removeExperience" />
              </div>
            </div>

            <div v-if="!!packageData?.PackageTravelExperienceInventory.length">
              <div class="input-holder wide has-dropdown">
                <label for="TravelExperienceInventoryIds">Additional Travel Experiences</label>
                <div class="select-holder">
                  <CustomMultiSelect id="TravelExperienceInventoryIds" name="TravelExperienceInventoryIds"
                    v-model="request.PackageTravelExperienceInventoryIds" display="chip" option-value="InventoryId"
                    :option-label="formatTravelExperience" :option-disabled="item => item.IsSoldOut"
                    :options="packageData.PackageTravelExperienceInventory" placeholder="Per fixture options" show-clear append-to="self">
                    <template #option="{ option }">
                      <span>{{ option.DisplayName }}: {{ option.FormattedDateRange }} ({{ formatCurrency(option.UnitRrpExclGst) }} pp) <span
                          v-if="option.IsSoldOut">- SOLD OUT</span></span>
                    </template>
                  </CustomMultiSelect>
                </div>
                <small class="p-error"
                  v-if="form.PackageTravelExperienceInventoryIds.$error">{{ form.PackageTravelExperienceInventoryIds.$errors[0].$message }}</small>
              </div>

              <div v-if="request.PackageTravelExperiences.length">
                <div class="asymmetric-input-group d-flex flex-v-centre extra-items no-lines desktop">
                  <div class="sub-option"></div>
                  <div class="input-holder">Number of People*</div>
                  <div class="input-holder">How many rooms?*</div>
                </div>
                <StartBookingTravelExperienceForm v-for="(experience, index) in request.PackageTravelExperiences"
                  :key="experience.TravelExperienceInventoryId" v-model="request.PackageTravelExperiences[index]"
                  :experience-metadata="travelExperienceMetadata[experience.TravelExperienceInventoryId]" @remove="removeTravelExperience" />
              </div>
            </div>

            <div class="form-subtotal" v-if="BigNumber(subtotal).gt(0)">
              <h3 class="text-uppercase">
                Subtotal: {{ formatCurrency(subtotal) }}
                <small v-if="packageData?.DomesticInternational == 'Domestic'" class="dm-sans text-mid-grey">+ GST</small>
              </h3>
            </div>
            <div class="form-button">
              <button type="submit" class="btn" :loading="isSubmitting || isSubmitted">Book now</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>
<script lang="ts" setup>
import { StartBookingExperienceRequestModel, StartBookingGameRequestModel, StartBookingTravelExperienceRequestModel, StartFlexibleBookingRequestModel, type RedirectUrlModel } from '@/components/BookingForm/types';
import type { ApiResult } from '@/types/ApiResult';
import type { BookingFlexiblePackageMetadataModel, BookingGameMetadataModel, PackageExperienceInventoryModel, PackageTravelExperienceInventoryModel } from '@/types/BookingGameMetadataModel';
import useVuelidate from '@vuelidate/core';
import { helpers, minLength, required } from '@vuelidate/validators';
import BigNumber from 'bignumber.js';
import { defaults, keyBy, last, values } from 'lodash';
import { computed, onMounted, ref, watch } from 'vue';
import wretch from 'wretch';
import CustomMultiSelect from '../CustomMultiSelect.vue';
import { formatCurrency, formatExperience, formatTravelExperience } from './formatters';
import StartBookingExperienceForm from './StartBookingExperienceForm.vue';
import StartBookingGameForm from './StartBookingGameForm.vue';
import StartBookingTravelExperienceForm from './StartBookingTravelExperienceForm.vue';

const props = defineProps({
  package: String,
});

const isSubmitting = ref<boolean>(false);
const isSubmitted = ref<boolean>(false);
const packageData = ref<BookingFlexiblePackageMetadataModel>();
const gameMetadata = computed<Record<number, BookingGameMetadataModel>>(() => keyBy(packageData.value?.Games, 'GameId'));
const gameOptions = computed(() => values(gameMetadata.value));
const experienceMetadata = computed<Record<number, PackageExperienceInventoryModel>>(() => keyBy(packageData.value?.PackageExperienceInventory, 'InventoryId'));
const travelExperienceMetadata = computed<Record<number, PackageTravelExperienceInventoryModel>>(() => keyBy(packageData.value?.PackageTravelExperienceInventory, 'InventoryId'));

function formatGame(game: BookingGameMetadataModel) {
  const suffix = game.IsSoldOut ? ' - SOLD OUT' : game.ComingSoon ? ' - COMING SOON' : '';
  return `${game.GameName}${suffix}`;
}

function isGameDisabled(game: BookingGameMetadataModel) {
  return game.IsSoldOut || game.ComingSoon;
}

const rules = computed<Partial<Record<keyof StartFlexibleBookingRequestModel, unknown>>>(() => ({
  GameIds: {
    required,
    min: helpers.withMessage('Required', minLength(1)),
  },
  PackageExperienceInventoryIds: {},
  PackageTravelExperienceInventoryIds: {},
}));
const request = ref<StartFlexibleBookingRequestModel>(defaults(new StartFlexibleBookingRequestModel({})));
const form = useVuelidate<StartFlexibleBookingRequestModel>(rules, request);

async function submit() {
  if (!(await form.value.$validate())) {
    return;
  }
  try {
    isSubmitting.value = true;
    const resp = await wretch('/api/booking/start-booking').post(request.value).json<ApiResult<RedirectUrlModel>>();
    if (resp.successful && resp.data) {
      window.location.href = resp.data.RedirectUrl;
      isSubmitted.value = true;
    }
  } finally {
    isSubmitting.value = false;
  }
}

const subtotal = computed(() => {
  let subtotal = new BigNumber(0);

  request.value.Games.forEach(rg => {
    const game = gameMetadata.value[rg.GameId]!;

    if (rg.HospitalityTicketInventoryId) {
      const hospitalityTicketInventory = game.HospitalityTicketInventory.find(x => x.InventoryId == rg.HospitalityTicketInventoryId);
      if (hospitalityTicketInventory && rg.NumberOfPeople) {
        if (!!hospitalityTicketInventory.ForcedQuantity) {
          subtotal = subtotal.plus(new BigNumber(hospitalityTicketInventory.UnitRrpExclGst));
        } else {
          subtotal = subtotal.plus(new BigNumber(hospitalityTicketInventory.UnitRrpExclGst).times(rg.NumberOfPeople));
        }
      }
    }

    if (rg.AccommodationInventoryId && rg.NumberOfRooms) {
      const accommodationInventory = game.AccommodationInventory.find(x => x.InventoryId == rg.AccommodationInventoryId);
      if (accommodationInventory && rg.NumberOfRooms) {
        subtotal = subtotal.plus(new BigNumber(accommodationInventory.UnitRrpExclGst).times(rg.NumberOfRooms));
      }
    }

    if (rg.ExperienceInventoryIds) {
      rg.Experiences.forEach(te => {
        const experienceInventory = game.ExperienceInventory.find(i => te.ExperienceInventoryId == i.InventoryId);
        if (experienceInventory && te.NumberOfPeople) {
          subtotal = subtotal.plus(new BigNumber(experienceInventory.UnitRrpExclGst).times(te.NumberOfPeople));
        }
      });
    }

    if (rg.TravelExperienceInventoryIds) {
      rg.TravelExperiences.forEach(te => {
        const travelExperienceInventory = game.TravelExperienceInventory.find(i => te.TravelExperienceInventoryId == i.InventoryId);
        if (travelExperienceInventory && te.NumberOfPeople) {
          subtotal = subtotal.plus(new BigNumber(travelExperienceInventory.UnitRrpExclGst).times(te.NumberOfPeople));
        }
      });
    }
  });

  if (packageData.value?.PackageExperienceInventory) {
    request.value.PackageExperiences.forEach(pe => {
      const experience = experienceMetadata.value[pe.ExperienceInventoryId];
      if (experience && pe.NumberOfPeople) {
        subtotal = subtotal.plus(new BigNumber(experience.UnitRrpExclGst).times(pe.NumberOfPeople));
      }
    });
  }

  if (packageData.value?.PackageTravelExperienceInventory) {
    request.value.PackageTravelExperiences.forEach(pe => {
      const experience = travelExperienceMetadata.value[pe.TravelExperienceInventoryId];
      if (experience && pe.NumberOfPeople) {
        subtotal = subtotal.plus(new BigNumber(experience.UnitRrpExclGst).times(pe.NumberOfPeople));
      }
    });
  }

  return subtotal.toString();
});

watch(
  () => request.value.GameIds,
  (gameIds: number[] | null) => {
    if (!gameIds) {
      gameIds = request.value.GameIds = [];
    }
    gameIds.forEach(gameId => {
      const game = request.value.Games.find(g => g.GameId == gameId);
      if (!game) {
        const lastGame = last(request.value.Games);
        request.value.Games.push(
          new StartBookingGameRequestModel({
            GameId: gameId,
            NumberOfPeople: lastGame?.NumberOfPeople,
            NumberOfRooms: lastGame?.NumberOfRooms,
          })
        );
      }
    });

    request.value.Games = request.value.Games.filter(g => gameIds.includes(g.GameId));
  },
  { deep: true }
);

watch(
  () => request.value.PackageExperienceInventoryIds,
  (inventoryIds: number[] | null) => {
    if (!inventoryIds) {
      inventoryIds = request.value.PackageExperienceInventoryIds = [];
    }
    inventoryIds.forEach(inventoryId => {
      const experience = request.value.PackageExperiences.find(g => g.ExperienceInventoryId == inventoryId);
      if (!experience) {
        const lastGame = last(request.value.Games);
        request.value.PackageExperiences.push(
          new StartBookingExperienceRequestModel({
            ExperienceInventoryId: inventoryId,
            NumberOfPeople: lastGame?.NumberOfPeople,
          })
        );
      }
    });

    request.value.PackageExperiences = request.value.PackageExperiences.filter(e => inventoryIds.includes(e.ExperienceInventoryId));
  },
  { deep: true }
);

function removeExperience(inventoryId: number) {
  const index = request.value.PackageExperienceInventoryIds.indexOf(inventoryId);
  if (index !== -1) {
    request.value.PackageExperienceInventoryIds.splice(index, 1);
  }
}

watch(
  () => request.value?.PackageTravelExperienceInventoryIds,
  (inventoryIds: number[] | undefined) => {
    if (inventoryIds === undefined) {
      inventoryIds = request.value.PackageTravelExperienceInventoryIds = [];
    }
    inventoryIds.forEach(inventoryId => {
      const experience = request.value.PackageTravelExperiences.find(g => g.TravelExperienceInventoryId == inventoryId);
      if (!experience) {
        const lastGame = last(request.value.Games);
        request.value.PackageTravelExperiences.push(
          new StartBookingTravelExperienceRequestModel({
            TravelExperienceInventoryId: inventoryId,
            NumberOfPeople: lastGame?.NumberOfPeople,
            NumberOfRooms: lastGame?.NumberOfRooms,
          })
        );
      }
    });

    request.value.PackageTravelExperiences = request.value.PackageTravelExperiences.filter(e => inventoryIds.includes(e.TravelExperienceInventoryId));
  },
  { deep: true }
);

function removeTravelExperience(inventoryId: number) {
  const index = request.value.PackageTravelExperienceInventoryIds.indexOf(inventoryId);
  if (index !== -1) {
    request.value.PackageTravelExperienceInventoryIds.splice(index, 1);
  }
}

onMounted(() => {
  packageData.value = props.package ? JSON.parse(props.package) : {};
  request.value.PackageId = packageData.value?.PackageId ?? 0;

  // get all the games where the tickets aren't sold out, filter to featured games if any
  if (packageData.value?.FeaturedGameIds.length) {
    request.value.GameIds = values(gameMetadata.value)
      .filter(g => !isGameDisabled(g) && packageData.value?.FeaturedGameIds.includes(g.GameId))
      .map(g => g.GameId);
  } else {
    request.value.GameIds = values(gameMetadata.value)
      .filter(g => !isGameDisabled(g))
      .map(g => g.GameId);
  }
});
</script>
